import React from "react";
import { graphql } from "react-relay";

import cx from "classnames";
import { compact } from "lodash";

import { smart } from "~/lib/relayUtil";
import Avatar from "~/ui/legacy/Avatar";

import {
	CheckboxInput,
	FaceInput,
	isCheckboxValue,
	isFaceValue,
	isRadioValue,
	isStarValue,
	isThumbValue,
	RadioInput,
	StarInput,
	ThumbInput,
} from "../FormInputs";
import { adaptQuestionChoiceToValue } from "../ResponseForm";
import type { Question } from "../ResponseForm/validation";
import { anonymizeFeedbackUser } from "../utils";
import type { Choice } from "./Question";

import type { FeedbackItemQuery$data as QueryResponse } from "~/__generated__/FeedbackItemQuery.graphql";
import type { PersonRequest$data as PersonRequest } from "~/__generated__/PersonRequest.graphql";

import stylesQuestion from "~/feedback/FeedbackItem/css/Question.module.css";

import styles from "./css/Person.module.css";

type Viewer = QueryResponse["viewer"];

interface Props {
	instance: PersonRequest;
	viewer: Viewer;
	questions: Question[];
	feedback?: {
		isAnonymous?: boolean;
		id?: string;
	};
}

export const renderUser = (user: Pick<PersonRequest["user"], "department" | "title" | "fullName" | "id">) => {
	const title = compact([user.department, user.title]).join(" - ");

	return (
		<div className={styles.userInformation}>
			<Avatar user={user} link={false} width="2.5em" />
			<div>
				<div className={styles.userName}>{user.fullName}</div>
				<div className={cx(styles.userTitle, stylesQuestion.noAnswer)}>{title}</div>
			</div>
		</div>
	);
};

class Person extends React.PureComponent<Props> {
	static Fragments = {
		instance: graphql`
			fragment PersonRequest on FeedbackRequestNode {
				id

				feedback {
					id
					isAnonymous
				}

				feedbackRecord {
					id
					contents
				}

				user {
					id
					email
					image
					fullName
					firstName
					lastName
					title
					department
					isGuest
				}
			}
		`,
	};

	renderTextAnswer = (question: Question, answer?: string) => {
		if (question.type !== "text") return;

		const { instance } = this.props;

		if (!answer) {
			return (
				<div className={cx(styles.noAnswer, stylesQuestion.noAnswer)}>
					{instance.feedback.isAnonymous ? "User" : instance.user.fullName} chose not to answer.
				</div>
			);
		}

		return <div className={styles.textAnswer}>{answer}</div>;
	};

	renderChoiceAnswer = (question: Question, answer?: Choice | Choice[]) => {
		if (question.type !== "choice") return;

		const { instance } = this.props;

		if (!answer) {
			return (
				<div className={cx(styles.noAnswer, stylesQuestion.noAnswer)}>
					{instance.feedback.isAnonymous ? "User" : instance.user.fullName} chose not to answer.
				</div>
			);
		}

		let component;
		const props = { readonly: true, name: `${question.id}-${instance.user.id}` };
		const value = adaptQuestionChoiceToValue(question, answer);

		if (question.appearance === "radio" && isRadioValue(value)) {
			component = <RadioInput {...props} value={value} options={question.choices} />;
		} else if (question.appearance === "checkbox" && isCheckboxValue(value)) {
			component = <CheckboxInput {...props} value={value} options={question.choices} />;
		} else if (isThumbValue(value)) {
			component = <ThumbInput {...props} value={value} />;
		} else if (isStarValue(value)) {
			component = <StarInput {...props} value={value} />;
		} else if (isFaceValue(value)) {
			component = <FaceInput {...props} value={value} />;
		}

		return component;
	};

	render() {
		const { questions, instance, viewer } = this.props;

		if (!instance || !instance.feedbackRecord || !instance.feedbackRecord.contents) {
			return <div>No record for this user - Empty state?</div>;
		}

		const answersByQuestion = instance.feedbackRecord.contents.reduce(
			(
				accum: { [key: string]: string | Choice | Choice[] },
				answer: { id: string; value: string | Choice | Choice[] },
			) => ({
				...accum,
				[answer.id]: answer.value,
			}),
			{},
		);

		return (
			<div className={cx(styles.wrapper, stylesQuestion.wrapper)}>
				{renderUser(
					this.props.feedback?.isAnonymous && viewer.user.id === instance.user.id
						? anonymizeFeedbackUser(this.props.feedback?.id ?? "", instance.user)
						: instance.user,
				)}
				{questions.map(question => {
					const { title: originalTitle } = question;

					const isIndented = originalTitle[0] === ">";
					const title = isIndented ? originalTitle.slice(1).trim() : originalTitle;

					return (
						<div key={question.id} className={cx(styles.container, { [styles.indented]: isIndented })}>
							<div
								className={cx(stylesQuestion.title, {
									[styles.titleHeading]: question.type === "blank",
									[stylesQuestion.titleHeading]: question.appearance === "text",
									[stylesQuestion.titleSubHeading]: question.appearance === "sub-text",
								})}
							>
								{title}
							</div>
							{this.renderTextAnswer(question, answersByQuestion[question.id])}
							{this.renderChoiceAnswer(question, answersByQuestion[question.id])}
						</div>
					);
				})}
			</div>
		);
	}
}

export default smart(Person);
